import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/api/client';
import type {
  ScrapeAnalyticsDrilldown,
  ScrapeAnalyticsListParams,
  ScrapeAnalyticsRun,
  ScrapeAnalyticsSourceRow,
} from '@/api/types/scrapeAnalytics';
import { CollapsiblePanel } from '@/components/common';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import { EmptyState, ErrorBanner, ExportButtons, FilterSelect, MetricChip, StatusBadge, Toolbar } from '@/components/ui';
import { formatTableError } from '@/components/table/formatCellValue';
import SourceChips from '@/components/ui/SourceChips';
import { useMe } from '@/context/MeContext';
import { useToast } from '@/components/Toast';
import { useWorkspaceScopedEffect } from '@/hooks/useWorkspaceScopedEffect';
import { SOURCE_FILTER_OPTIONS } from '@/utils/sourceFilterOptions';
import { sourceDisplayName } from '@/utils/sourceDisplayName';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'running', label: 'Running' },
  { value: 'queued', label: 'Queued' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'vehicle', label: 'Vehicle' },
];

function formatDuration(seconds?: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function outcomeVariant(code?: string | null): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  switch (code) {
    case 'new_links_found':
      return 'success';
    case 'job_failed':
    case 'dead_letter':
      return 'danger';
    case 'no_job_created':
    case 'interval_skipped':
    case 'zero_links_extracted':
    case 'filters_rejected_all':
    case 'relevance_rejected_all':
      return 'warning';
    case 'job_in_progress':
      return 'info';
    default:
      return 'neutral';
  }
}

function outcomeLabel(code?: string | null): string {
  switch (code) {
    case 'no_runs':
      return 'No runs';
    case 'no_job_created':
      return 'No job created';
    case 'interval_skipped':
      return 'Skipped';
    case 'job_in_progress':
      return 'In progress';
    case 'job_failed':
      return 'Job failed';
    case 'zero_links_extracted':
      return '0 links extracted';
    case 'filters_rejected_all':
      return 'Filters rejected all';
    case 'relevance_rejected_all':
      return 'Relevance rejected all';
    case 'duplicates_only':
      return 'Duplicates only';
    case 'new_links_found':
      return 'New links found';
    case 'dead_letter':
      return 'Dead letter';
    case 'no_new_links':
      return 'No new links';
    default:
      return code || 'Unknown';
  }
}

function buildParams(
  searchName: string,
  sourceFilter: string,
  statusFilter: string,
  categoryFilter: string,
  dateFrom: string,
  dateTo: string
): ScrapeAnalyticsListParams {
  return {
    search_name: searchName || undefined,
    source: sourceFilter || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    started_after: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
    started_before: dateTo ? new Date(`${dateTo}T23:59:59`).toISOString() : undefined,
  };
}

function MobileDeDebugBanner({
  drilldown,
  canForceScrape,
  forcing,
  onForceScrape,
}: {
  drilldown: ScrapeAnalyticsDrilldown;
  canForceScrape: boolean;
  forcing: boolean;
  onForceScrape?: () => void;
}) {
  const latest = drilldown.runs[0];
  const isMobile = drilldown.source_name === 'mobile_de';
  const isSkipped =
    drilldown.outcome_code === 'interval_skipped' ||
    Boolean(latest?.scrape_skipped) ||
    latest?.status === 'skipped';

  return (
    <div className={`scrape-outcome-banner scrape-outcome-banner--${outcomeVariant(drilldown.outcome_code)}`}>
      <div className="scrape-outcome-banner__header">
        <strong>
          {isMobile ? 'Mobile.de diagnosis' : `${sourceDisplayName(drilldown.source_name)} diagnosis`}
        </strong>
        <StatusBadge variant={outcomeVariant(drilldown.outcome_code)}>
          {outcomeLabel(drilldown.outcome_code)}
        </StatusBadge>
      </div>
      <p className="scrape-outcome-banner__message">{drilldown.outcome_message}</p>
      {isSkipped ? (
        <p className="muted">
          Scrape did not run — link counts below are not from a fetch. Use force scrape to bypass the min interval.
        </p>
      ) : null}
      {canForceScrape && isMobile && onForceScrape ? (
        <p>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={forcing}
            onClick={onForceScrape}
          >
            {forcing ? 'Queueing force scrape…' : 'Force scrape now'}
          </button>
        </p>
      ) : null}
      {latest ? (
        <dl className="detail-grid scrape-outcome-banner__lifecycle">
          <dt>Job created</dt>
          <dd>{latest.job_id ? 'Yes' : 'No — source was skipped or not scheduled'}</dd>
          <dt>Job started</dt>
          <dd>{latest.started_at ? formatTime(latest.started_at) : 'Not started'}</dd>
          <dt>Job completed</dt>
          <dd>{latest.finished_at ? formatTime(latest.finished_at) : latest.status === 'running' ? 'Still running' : '—'}</dd>
          {!isSkipped ? (
            <>
              <dt>Raw links</dt>
              <dd>{latest.raw_links_count}</dd>
              <dt>After filters</dt>
              <dd>{latest.after_vehicle_filter_count}</dd>
              <dt>New links</dt>
              <dd>{latest.new_links_count}</dd>
            </>
          ) : null}
          {latest.error ? (
            <>
              <dt>Error</dt>
              <dd className="text-danger">{formatTableError(latest.error)}</dd>
            </>
          ) : null}
          {latest.skip_reason ? (
            <>
              <dt>Skip reason</dt>
              <dd>{latest.skip_reason}</dd>
            </>
          ) : null}
        </dl>
      ) : (
        <p className="muted">No scrape runs in this period — Mobile.de did not get a job or no executions were recorded.</p>
      )}
    </div>
  );
}

export default function ScrapeAnalyticsPanel({ refreshSignal }: { refreshSignal?: unknown }) {
  const { me } = useMe();
  const { toast } = useToast();
  const canManageJobs = me?.permissions.canManageJobs ?? false;
  const [searchName, setSearchName] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceRows, setSourceRows] = useState<ScrapeAnalyticsSourceRow[]>([]);
  const [searchRows, setSearchRows] = useState<ScrapeAnalyticsSourceRow[]>([]);
  const [drilldown, setDrilldown] = useState<ScrapeAnalyticsDrilldown | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forcing, setForcing] = useState(false);
  const loadedRef = useRef(false);

  useWorkspaceScopedEffect(() => {
    loadedRef.current = false;
    setSourceRows([]);
    setSearchRows([]);
    setDrilldown(null);
    setSelectedSource(null);
    setError(null);
  });

  const listParams = useMemo(
    () => buildParams(searchName, sourceFilter, statusFilter, categoryFilter, dateFrom, dateTo),
    [searchName, sourceFilter, statusFilter, categoryFilter, dateFrom, dateTo]
  );

  const load = useCallback(async (background = false) => {
    const keepVisible = background && loadedRef.current;
    if (!keepVisible) {
      setLoading(true);
    }
    setError(null);
    try {
      const [bySource, bySearch] = await Promise.all([
        api.scrapeAnalytics.bySource(listParams),
        api.scrapeAnalytics.bySearch(listParams),
      ]);
      setSourceRows(bySource.items);
      setSearchRows(bySearch.items);
      loadedRef.current = bySource.items.length > 0 || bySearch.items.length > 0;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load scrape analytics');
    } finally {
      if (!keepVisible) {
        setLoading(false);
      }
    }
  }, [listParams]);

  const loadDrilldown = useCallback(
    async (source: string, search?: string | null) => {
      setSelectedSource(source);
      try {
        const data = await api.scrapeAnalytics.drilldown({
          ...listParams,
          source,
          search_name: search || listParams.search_name,
        });
        setDrilldown(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load drilldown');
        setDrilldown(null);
      }
    },
    [listParams]
  );

  const handleForceScrape = useCallback(async () => {
    if (!drilldown) return;
    setForcing(true);
    setError(null);
    try {
      const result = await api.searchExecutions.runDiagnosticsScrape({
        source: drilldown.source_name,
        search_name: drilldown.search_name || searchName || undefined,
        force: true,
      });
      toast(
        result.jobs_queued > 0
          ? `Force scrape queued (${result.jobs_queued} job${result.jobs_queued === 1 ? '' : 's'})`
          : 'Force scrape request accepted',
        'success'
      );
      await load();
      await loadDrilldown(drilldown.source_name, drilldown.search_name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to queue force scrape');
    } finally {
      setForcing(false);
    }
  }, [drilldown, searchName, toast, load, loadDrilldown]);

  useEffect(() => {
    loadedRef.current = false;
    void load(false);
  }, [load]);

  useEffect(() => {
    if (refreshSignal == null) return;
    void load(true);
  }, [load, refreshSignal]);

  useEffect(() => {
    if (selectedSource) {
      void loadDrilldown(selectedSource);
    } else {
      setDrilldown(null);
    }
  }, [selectedSource, loadDrilldown]);

  const sourceColumns = useMemo<DataTableColumn<ScrapeAnalyticsSourceRow>[]>(
    () => [
      {
        id: 'source',
        header: 'Source',
        width: 130,
        render: (row) => <SourceChips sources={[row.source_name]} variant="compact" />,
      },
      {
        id: 'outcome',
        header: 'Outcome',
        width: 140,
        render: (row) => (
          <StatusBadge variant={outcomeVariant(row.outcome_code)}>{outcomeLabel(row.outcome_code)}</StatusBadge>
        ),
      },
      {
        id: 'runs',
        header: 'Runs',
        width: 60,
        render: (row) => row.run_count,
      },
      {
        id: 'success',
        header: 'OK',
        width: 50,
        render: (row) => row.success_count,
      },
      {
        id: 'failed',
        header: 'Failed',
        width: 60,
        render: (row) => row.failed_count,
      },
      {
        id: 'partial',
        header: 'Partial',
        width: 60,
        render: (row) => row.partial_failed_count,
      },
      {
        id: 'dead_letter',
        header: 'Dead letter',
        width: 80,
        render: (row) => row.dead_letter_count,
      },
      {
        id: 'avg_duration',
        header: 'Avg',
        width: 70,
        render: (row) => formatDuration(row.avg_duration_seconds),
      },
      {
        id: 'max_duration',
        header: 'Max',
        width: 70,
        render: (row) => formatDuration(row.max_duration_seconds),
      },
      {
        id: 'raw',
        header: 'Raw',
        width: 50,
        render: (row) => row.raw_links_total,
      },
      {
        id: 'relevance',
        header: 'Relevance',
        width: 80,
        render: (row) => row.after_relevance_total,
      },
      {
        id: 'vehicle',
        header: 'Vehicle filt.',
        width: 90,
        render: (row) => row.after_vehicle_filter_total,
      },
      {
        id: 'new',
        header: 'New',
        width: 50,
        render: (row) => row.new_links_total,
      },
      {
        id: 'dupes',
        header: 'Dupes',
        width: 60,
        render: (row) => row.duplicates_skipped_total,
      },
      {
        id: 'filtered',
        header: 'Filtered',
        width: 70,
        render: (row) => row.filtered_out_total,
      },
      {
        id: 'unsupported',
        header: 'Unsup. filt.',
        width: 90,
        render: (row) => row.unsupported_filters_total,
      },
      {
        id: 'last_run',
        header: 'Last run',
        width: 140,
        render: (row) => formatTime(row.last_run_at),
      },
    ],
    []
  );

  const searchSourceColumns = useMemo<DataTableColumn<ScrapeAnalyticsSourceRow>[]>(
    () => [
      {
        id: 'search',
        header: 'Search',
        render: (row) => row.search_name || '—',
      },
      ...sourceColumns.slice(0, 1),
      ...sourceColumns.slice(2),
    ],
    [sourceColumns]
  );

  const runColumns = useMemo<DataTableColumn<ScrapeAnalyticsRun>[]>(
    () => [
      {
        id: 'when',
        header: 'When',
        render: (row) => formatTime(row.finished_at || row.started_at || row.created_at),
      },
      {
        id: 'search',
        header: 'Search',
        render: (row) => row.search_name || '—',
      },
      {
        id: 'status',
        header: 'Status',
        render: (row) => (
          <StatusBadge variant={outcomeVariant(row.status === 'success' ? 'new_links_found' : row.outcome_code)}>
            {row.status || '—'}
          </StatusBadge>
        ),
      },
      {
        id: 'outcome',
        header: 'Outcome',
        render: (row) => (
          <StatusBadge variant={outcomeVariant(row.outcome_code)}>{outcomeLabel(row.outcome_code)}</StatusBadge>
        ),
      },
      {
        id: 'raw',
        header: 'Raw',
        render: (row) => row.raw_links_count,
      },
      {
        id: 'filtered',
        header: 'Filtered',
        render: (row) => row.filtered_out_count,
      },
      {
        id: 'new',
        header: 'New',
        render: (row) => row.new_links_count,
      },
      {
        id: 'duration',
        header: 'Duration',
        render: (row) => formatDuration(row.duration_seconds),
      },
      {
        id: 'error',
        header: 'Error',
        render: (row) => (
          <TruncatedCell value={formatTableError(row.error || row.skip_reason)} maxWidth={240} />
        ),
      },
    ],
    []
  );

  return (
    <>
      <CollapsiblePanel
        id="scrape-analytics"
        storageScope="pipeline-analytics"
        title="Scrape performance analytics"
        subtitle="Compare sources and diagnose why Mobile.de (or others) sent no links"
        defaultExpanded
      >
        <Toolbar>
          <label className="filter-field">
            <span className="filter-label">Search name</span>
            <input
              type="search"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="e.g. DEBUG BMW"
            />
          </label>
          <FilterSelect label="Source" htmlFor="analytics-source">
            <select
              id="analytics-source"
              className="filter-select"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              {SOURCE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="Status" htmlFor="analytics-status">
            <select
              id="analytics-status"
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="Category" htmlFor="analytics-category">
            <select
              id="analytics-category"
              className="filter-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="From" htmlFor="analytics-date-from">
            <input
              id="analytics-date-from"
              type="date"
              className="filter-select"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterSelect>
          <FilterSelect label="To" htmlFor="analytics-date-to">
            <input
              id="analytics-date-to"
              type="date"
              className="filter-select"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterSelect>
        </Toolbar>

        {error ? <ErrorBanner message={error} onDismiss={() => setError(null)} /> : null}

        {loading && sourceRows.length === 0 ? (
          <EmptyState title="Loading analytics…" description="Aggregating scrape runs from search executions." />
        ) : sourceRows.length === 0 ? (
          <EmptyState
            title="No scrape analytics yet"
            description="Run DEBUG BMW searches or scheduled scrapes to populate analytics."
          />
        ) : (
          <>
            <div className="metric-chips metric-chips--wrap">
              {sourceRows.map((row) => (
                <MetricChip
                  key={row.source_name}
                  label={sourceDisplayName(row.source_name)}
                  value={row.new_links_total}
                  variant={row.new_links_total > 0 ? 'success' : row.failed_count > 0 ? 'danger' : 'neutral'}
                />
              ))}
            </div>

            <CollapsiblePanel
              id="source-comparison"
              storageScope="pipeline-analytics"
              title="Source comparison"
              defaultExpanded
              unmountWhenCollapsed
            >
              <DataTable
                columns={sourceColumns}
                rows={sourceRows}
                getRowKey={(row) => row.source_name}
                onRowClick={(row) => {
                  setSelectedSource(row.source_name === selectedSource ? null : row.source_name);
                }}
                columnStorageKey="scrape-analytics-sources"
                toolbar={{
                  searchPlaceholder: 'Filter sources…',
                  onRefresh: () => void load(),
                  rightSlot: (
                    <ExportButtons path="/exports/analytics/scrape/sources" params={listParams} />
                  ),
                }}
                emptyState={<EmptyState title="No sources" description="Adjust filters to see source metrics." />}
              />
            </CollapsiblePanel>

            <CollapsiblePanel
              id="search-source-breakdown"
              storageScope="pipeline-analytics"
              title="Search × source breakdown"
              defaultExpanded={false}
              unmountWhenCollapsed
            >
              <DataTable
                columns={searchSourceColumns}
                rows={searchRows}
                getRowKey={(row) => `${row.search_name}-${row.source_name}`}
                onRowClick={(row) => void loadDrilldown(row.source_name, row.search_name)}
                columnStorageKey="scrape-analytics-by-search"
                toolbar={{
                  searchPlaceholder: 'Filter rows…',
                  onRefresh: () => void load(),
                  rightSlot: (
                    <ExportButtons path="/exports/analytics/scrape/by-search" params={listParams} />
                  ),
                }}
                emptyState={<EmptyState title="No rows" description="No search/source combinations match filters." />}
              />
            </CollapsiblePanel>
          </>
        )}
      </CollapsiblePanel>

      {drilldown ? (
        <CollapsiblePanel
          id="scrape-drilldown"
          storageScope="pipeline-analytics"
          title={`Drilldown — ${sourceDisplayName(drilldown.source_name)}${drilldown.search_name ? ` / ${drilldown.search_name}` : ''}`}
          defaultExpanded
        >
          <MobileDeDebugBanner
            drilldown={drilldown}
            canForceScrape={canManageJobs}
            forcing={forcing}
            onForceScrape={() => void handleForceScrape()}
          />
          {drilldown.summary ? (
            <div className="metric-chips metric-chips--wrap">
              <MetricChip label="Runs" value={drilldown.summary.run_count} />
              <MetricChip label="Raw links" value={drilldown.summary.raw_links_total} />
              <MetricChip label="After relevance" value={drilldown.summary.after_relevance_total} />
              <MetricChip label="After vehicle filt." value={drilldown.summary.after_vehicle_filter_total} />
              <MetricChip label="New links" value={drilldown.summary.new_links_total} variant="success" />
              <MetricChip label="Duplicates" value={drilldown.summary.duplicates_skipped_total} />
              <MetricChip label="Filtered out" value={drilldown.summary.filtered_out_total} variant="warning" />
            </div>
          ) : null}
          <DataTable
            columns={runColumns}
            rows={drilldown.runs}
            getRowKey={(row) => row.id}
            columnStorageKey="scrape-analytics-runs"
            toolbar={{
              searchPlaceholder: 'Filter runs…',
              rightSlot: drilldown ? (
                <ExportButtons
                  path="/exports/analytics/scrape/sources"
                  params={{ ...listParams, source: drilldown.source_name }}
                />
              ) : undefined,
            }}
            emptyState={<EmptyState title="No runs" description="No individual runs for this drilldown." />}
          />
        </CollapsiblePanel>
      ) : null}
    </>
  );
}
